import { GetPopUpResponse, PopUpAction, PopUpTrigger } from '../types';
import { LitElement, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

import { deletePopup } from '../helpers/api/popups-api';

enum PopUpSortField {
	Name = 'name',
	Priority = 'priority',
	Type = 'type'
}

@customElement('existing-pop-ups-component')
export class ExistingPopUpsComponent extends LitElement {
	@property({ type: String })
	organizationId: string = '';

	@property({ type: Array })
	popups: GetPopUpResponse[] = [];

	@property({ type: Boolean })
	loading: boolean = false;

	@property({ type: String })
	error: string = '';

	@property({ type: Boolean })
	isPopupBeingEdited: boolean = false;

	@state()
	private sortBy: PopUpSortField = PopUpSortField.Priority;

	@state()
	private sortOrder: 'asc' | 'desc' = 'desc';

	@state()
	private selectedTab: number = 0;

	private static readonly TAB_OPTIONS = ['Live', 'Drafts'];

	private get filteredPopups(): GetPopUpResponse[] {
		const filtered = this.popups.filter(popup => {
			if (this.selectedTab === 0) return popup.is_active; // Live
			return !popup.is_active; // Drafts
		});

		// Sort
		filtered.sort((a, b) => {
			let comparison = 0;

			if (this.sortBy === PopUpSortField.Name) {
				const nameA = a.name.toLowerCase();
				const nameB = b.name.toLowerCase();
				comparison = nameA.localeCompare(nameB);
			} else if (this.sortBy === PopUpSortField.Priority) {
				comparison = a.priority - b.priority;
			} else if (this.sortBy === PopUpSortField.Type) {
				const typeA = this.getPopUpTypeLabel(a);
				const typeB = this.getPopUpTypeLabel(b);
				comparison = typeA.localeCompare(typeB);
			}

			return this.sortOrder === 'asc' ? comparison : -comparison;
		});

		return filtered;
	}

	updated(changedProperties: Map<string, any>) {
		if (changedProperties.has('popups') ||
			changedProperties.has('sortBy') ||
			changedProperties.has('sortOrder') ||
			changedProperties.has('selectedTab')) {
			this.requestUpdate();
		}
	}

	private changeTab(e: CustomEvent) {
		this.selectedTab = e.detail.tabIndex;
	}

	private sortClickHandler(sortBy: PopUpSortField) {
		if (this.sortBy === sortBy) {
			this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
		} else {
			this.sortBy = sortBy;
			this.sortOrder = 'desc';
		}
	}

	private async handleEdit(popup: GetPopUpResponse) {
		this.dispatchEvent(new CustomEvent('set-edited-popup', {
			detail: { popup },
			bubbles: true,
			composed: true,
		}));
	}

	private async handleDelete(popup: GetPopUpResponse) {
		if (!confirm(`Delete popup "${popup.name}"?`)) {
			return;
		}

		try {
			const callsite = 'existing-pop-ups-component';
			await deletePopup(callsite, this.organizationId, popup._id.$oid);
			this.popups = this.popups.filter(p => p._id.$oid !== popup._id.$oid);
		} catch (err) {
			this.error = 'Failed to delete popup';
			console.error(err);
		}
	}

	private handlePreview(popup: GetPopUpResponse) {
		this.dispatchEvent(new CustomEvent('preview-popup', {
			detail: { popup },
			bubbles: true,
			composed: true,
		}));
	}

	private handleViewAnalytics(popup: GetPopUpResponse) {
		this.dispatchEvent(new CustomEvent('view-popup-analytics', {
			detail: { popup_id: popup._id.$oid },
			bubbles: true,
			composed: true,
		}));
	}

	private getPopUpTypeLabel(popup: GetPopUpResponse): string {
		if (popup.pop_up_type === PopUpTrigger.OnMapLoad) {
			return 'On Map Load';
		}

		if (popup.pop_up_type === PopUpTrigger.LogicBased) {
			const hasGeo = popup.geo_trigger != null;
			const hasTime = popup.active_from != null || popup.active_until != null;

			if (hasGeo && hasTime) {
				return 'Logic Based (Geo + Time)';
			} else if (hasGeo) {
				return 'Logic Based (Geofence)';
			} else if (hasTime) {
				return 'Logic Based (Time)';
			} else {
				return 'Logic Based';
			}
		}

		return 'Post Submission';
	}

	private getActionTypeLabel(action: PopUpAction): string {
		if (action.type === 'none') return 'Info Only';
		if (action.type === 'collect-email') return 'Email Capture';
		if (action.type === 'link-out') return 'Link Out';
		if (action.type === 'open-point') return 'Open Point';
		return 'Unknown';
	}

	render() {
		return html`
			${this.error ? html`<div class="error">${this.error}</div>` : ''}

			<tabs-component
				id="tabsComponent"
				.options=${ExistingPopUpsComponent.TAB_OPTIONS}
				.type=${'pill'}
				style="width: 100%; margin-bottom: 10px; margin-top: 10px;"
				.tabIndex=${this.selectedTab}
				@on-select-tab=${(e: CustomEvent) => this.changeTab(e)}
			></tabs-component>

			${this.loading
				? html`<div class="loading">Loading popups...</div>`
				: this.filteredPopups.length === 0
					? html`<div class="empty-state">No popups found</div>`
					: html`
						<div class="popups-wrapper">
							<div class="header-row">
								<div class="header-cell text" @click=${() => this.sortClickHandler(PopUpSortField.Name)}>
									Name ${this.sortBy === PopUpSortField.Name ? html`<mwc-icon>${this.sortOrder === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}</mwc-icon>` : ''}
								</div>
								<div class="header-cell number" @click=${() => this.sortClickHandler(PopUpSortField.Type)}>
									Type ${this.sortBy === PopUpSortField.Type ? html`<mwc-icon>${this.sortOrder === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}</mwc-icon>` : ''}
								</div>
								<div class="header-cell number">Action</div>
								<div class="header-cell number" @click=${() => this.sortClickHandler(PopUpSortField.Priority)}>
									Priority ${this.sortBy === PopUpSortField.Priority ? html`<mwc-icon>${this.sortOrder === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}</mwc-icon>` : ''}
								</div>
								<div class="header-cell number">Active Period</div>
								<div class="header-cell number">Actions</div>
							</div>
							${this.filteredPopups.map(
								popup => html`
									<div class="popup-row">
										<div class="data-cell text">
											<div class="popup-name">${popup.name}</div>
										</div>
										<div class="data-cell number">${this.getPopUpTypeLabel(popup)}</div>
										<div class="data-cell number">${this.getActionTypeLabel(popup.action)}</div>
										<div class="data-cell number">${popup.priority}</div>
										<div class="data-cell number">
											${popup.active_from ?? popup.active_until
												? html`<small>${popup.active_from ? new Date(popup.active_from).toLocaleDateString() : 'Start'} - ${popup.active_until ? new Date(popup.active_until).toLocaleDateString() : 'No end'}</small>`
												: html`<small>Always</small>`}
										</div>
										<div class="data-cell number" style="display: flex; flex-wrap: wrap; justify-content: flex-start; gap: 2px;">
											<form-button variant="outline" size="xs" icon="visibility" class="aqua-blue" @click=${() => this.handlePreview(popup)}>Preview</form-button>
											<form-button variant="outline" size="xs" icon="edit" class="aqua-blue" ?disabled=${this.isPopupBeingEdited} @click=${async () => await this.handleEdit(popup)}>Edit</form-button>
											<form-button variant="outline" size="xs" icon="delete" class="aqua-blue" ?disabled=${this.isPopupBeingEdited} @click=${async () => await this.handleDelete(popup)}>Delete</form-button>
											<form-button variant="outline" size="xs" icon="bar_chart" class="aqua-blue" ?disabled=${this.isPopupBeingEdited} @click=${() => this.handleViewAnalytics(popup)}>Stats</form-button>
										</div>
									</div>
								`
							)}
						</div>
					`}
		`;
	}

	static get styles() {
		return css`
			:host {
				font-size: 12px;
				display: flex;
				flex-direction: column;
			}

			.error {
				background-color: #f8d7da;
				color: #721c24;
				padding: 12px;
				border-radius: 4px;
				margin-bottom: 20px;
			}

			.loading {
				text-align: center;
				padding: 40px;
				color: #6c757d;
			}

			.empty-state {
				text-align: center;
				padding: 40px;
				color: #6c757d;
			}

			.header-row {
				cursor: pointer;
				width: 100%;
				align-items: center;
				font-weight: 700;
				display: flex;
				height: 40px;
				background-color: var(--theme-border-color);
				gap: 10px;
			}

			.header-row mwc-icon {
				font-size: 20px;
			}

			.header-cell,
			.data-cell {
				align-items: center;
			}

			.header-cell {
				display: flex;
				align-items: center;
				padding: 0 10px;
			}

			.header-cell.text {
				text-align: left;
				width: 25%;
				overflow: hidden;
				text-overflow: ellipsis;
			}

			.header-cell.number {
				text-align: center;
				width: 12.5%;
				justify-content: center;
			}

			.data-cell {
				display: flex;
				align-items: center;
				padding: 0 10px;
			}

			.data-cell.text {
				text-align: left;
				width: 25%;
				overflow: hidden;
				text-overflow: ellipsis;
			}

			.data-cell.number {
				text-align: center;
				width: 12.5%;
				justify-content: center;
			}

			.popups-wrapper {
				width: 100%;
				display: flex;
				flex-direction: column;
				align-items: center;
				justify-content: flex-start;
				box-sizing: border-box;
			}

			.popup-row {
				width: 100%;
				display: flex;
				align-items: center;
				justify-content: space-between;
				padding: 8px 5px;
				box-sizing: border-box;
				border-bottom: 1px solid #DADADA;
				gap: 10px;
			}

			.popup-row:hover {
				background-color: var(--theme-border-color);
				cursor: context-menu;
			}

			.popup-name {
				font-weight: 500;
				color: #102C3D;
			}

			.popup-header {
				font-size: 11px;
				color: #6c757d;
				margin-top: 2px;
			}

			.aqua-blue {
				--mdc-theme-primary: var(--main-secondary-color);
			}
		`;
	}
}
